import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import { useLanguage } from '../contexts/LanguageContext'
import { useRestaurant } from '../contexts/RestaurantContext'
import { customerApi } from '../services/api'
import { addRecentReservation } from '../utils/recentActivity'

const MIN_GUESTS = 1
const MAX_GUESTS = 20
const DEFAULT_OPENING = '16:00'
const DEFAULT_CLOSING = '22:00'
/** Reservations must be at least this many hours before closing time. */
const HOURS_BEFORE_CLOSE = 1

/** Parse "HH:mm" to minutes from midnight. Returns 0 if invalid. */
function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr || typeof timeStr !== 'string') return 0
  const parts = timeStr.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!parts) return 0
  const h = Math.min(23, Math.max(0, parseInt(parts[1], 10)))
  const m = Math.min(59, Math.max(0, parseInt(parts[2], 10)))
  return h * 60 + m
}

/** Build time slots between opening and closing; supports overnight. Last slot is at least 1 hour before close. */
function buildTimeSlots(
  durationMinutes: number,
  openingTime: string,
  closingTime: string
): string[] {
  const slots: string[] = []
  const startMin = parseTimeToMinutes(openingTime) || parseTimeToMinutes(DEFAULT_OPENING)
  const endMin = parseTimeToMinutes(closingTime) ?? parseTimeToMinutes(DEFAULT_CLOSING)
  const duration = Math.max(15, Math.min(60, durationMinutes))
  const cutoffMin = 60 * HOURS_BEFORE_CLOSE // last slot must be at least this many minutes before close

  if (endMin > startMin) {
    // Same-day range (e.g. 09:00–22:00): only slots where slot + 1hr <= close
    const lastSlotMax = Math.max(startMin, endMin - cutoffMin)
    for (let totalMin = startMin; totalMin <= lastSlotMax; totalMin += duration) {
      const h = Math.floor(totalMin / 60)
      const m = totalMin % 60
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
    }
  } else if (endMin < startMin) {
    // Overnight (e.g. 16:00 to 01:00): before midnight end at 23:00 (1hr before midnight), after midnight end at close - 1hr
    const lastBeforeMidnight = 24 * 60 - cutoffMin // 23:00
    for (let totalMin = startMin; totalMin <= lastBeforeMidnight; totalMin += duration) {
      const h = Math.floor(totalMin / 60)
      const m = totalMin % 60
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
    }
    const lastAfterMidnight = Math.max(0, endMin - cutoffMin)
    for (let totalMin = 0; totalMin <= lastAfterMidnight; totalMin += duration) {
      const h = Math.floor(totalMin / 60)
      const m = totalMin % 60
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
    }
  }
  // else open === close: no bookable range
  return slots
}

/** True if this slot is after midnight in an overnight shift (same calendar day as open is yesterday). */
function isOvernightSlot(slot: string, openMinutes: number): boolean {
  const slotMin = parseTimeToMinutes(slot)
  return slotMin < openMinutes
}

/** Get the actual datetime for a slot on the selected date (adds one day for overnight slots). */
function getSlotDateTime(
  selectedDate: Date,
  slot: string,
  daySchedule: DaySchedule
): Date {
  const [h, m] = slot.split(':').map(Number)
  const d = new Date(selectedDate)
  const openMin = parseTimeToMinutes(daySchedule.open)
  if (isOvernightSlot(slot, openMin)) {
    d.setDate(d.getDate() + 1)
  }
  d.setHours(h, m, 0, 0)
  return d
}

type DaySchedule = { open: string; close: string; closed: boolean }

function getScheduleForDay(
  schedule: Record<string, DaySchedule> | undefined,
  dayIndex: number
): DaySchedule {
  const key = String(dayIndex)
  const slot = schedule?.[key]
  if (slot && typeof slot === 'object')
    return { open: slot.open || DEFAULT_OPENING, close: slot.close || DEFAULT_CLOSING, closed: !!slot.closed }
  return { open: DEFAULT_OPENING, close: DEFAULT_CLOSING, closed: false }
}

function dateToYYYYMMDD(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Format "HH:mm" (24h) as "h:mm AM/PM". */
function formatTime12h(time24: string): string {
  const parts = time24.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!parts) return time24
  let h = parseInt(parts[1], 10)
  const m = parts[2]
  const ampm = h >= 12 ? 'PM' : 'AM'
  if (h === 0) h = 12
  else if (h > 12) h -= 12
  return `${h}:${m} ${ampm}`
}

const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** Dates from today through the next 14 days (15 days total). */
function getDateRange(): Date[] {
  const dates: Date[] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  for (let i = 0; i < 15; i++) {
    const d = new Date(today)
    d.setDate(today.getDate() + i)
    dates.push(d)
  }
  return dates
}

export default function Reservation() {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const { restaurantInfo } = useRestaurant()
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [guests, setGuests] = useState(2)
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  })
  const [selectedTime, setSelectedTime] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const durationMinutes = Number(restaurantInfo?.reservation_timeslot_duration_minutes) || 30
  const rawMinHours = restaurantInfo?.reservation_minimum_booking_hours
  const minimumBookingHours = (rawMinHours !== undefined && rawMinHours !== null)
    ? Math.max(0, Number(rawMinHours))
    : 2
  const schedule = restaurantInfo?.restaurant_schedule
  const holidays = useMemo(() => new Set(restaurantInfo?.restaurant_holidays ?? []), [restaurantInfo?.restaurant_holidays])

  /** For the selected date: closed if holiday or that day is marked closed in schedule. */
  const selectedDayClosed = useMemo(() => {
    if (!selectedDate) return true
    const yyyyMmDd = dateToYYYYMMDD(selectedDate)
    if (holidays.has(yyyyMmDd)) return true
    const daySchedule = getScheduleForDay(schedule, selectedDate.getDay())
    return daySchedule.closed
  }, [selectedDate, schedule, holidays])

  /** Schedule for the selected day (for overnight slot logic). */
  const selectedDaySchedule = useMemo(
    () => (selectedDate ? getScheduleForDay(schedule, selectedDate.getDay()) : null),
    [selectedDate, schedule]
  )

  /** Time slots for the selected date (empty if closed). */
  const timeSlots = useMemo(() => {
    if (!selectedDate || selectedDayClosed || !selectedDaySchedule) return []
    return buildTimeSlots(durationMinutes, selectedDaySchedule.open, selectedDaySchedule.close)
  }, [selectedDate, selectedDayClosed, selectedDaySchedule, durationMinutes])

  const dateRange = useMemo(() => getDateRange(), [])
  const todayStart = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  }, [])

  /** Slots before this time (on today) are too soon to book. */
  const minBookingCutoff = useMemo(() => {
    const hours = Number(minimumBookingHours)
    const cut = new Date()
    cut.setHours(cut.getHours() + hours, cut.getMinutes(), 0, 0)
    return cut.getTime()
  }, [minimumBookingHours])

  // When selected day is closed, clear time
  useEffect(() => {
    if (selectedDayClosed) setSelectedTime('')
  }, [selectedDayClosed])

  // When slots or selected date change, pick first available slot if current selection is invalid or empty
  const todayStr = dateToYYYYMMDD(new Date())
  useEffect(() => {
    if (!timeSlots.length || !selectedDate || selectedDayClosed || !selectedDaySchedule) return
    const isSelectedAvailable = timeSlots.some((slot) => {
      const slotDate = getSlotDateTime(selectedDate, slot, selectedDaySchedule)
      const slotTs = slotDate.getTime()
      if (dateToYYYYMMDD(slotDate) === todayStr && slotTs < minBookingCutoff) return false
      return true
    })
    if (!selectedTime || !timeSlots.includes(selectedTime) || !isSelectedAvailable) {
      const firstAvailable = timeSlots.find((slot) => {
        const slotDate = getSlotDateTime(selectedDate, slot, selectedDaySchedule)
        const slotTs = slotDate.getTime()
        if (dateToYYYYMMDD(slotDate) === todayStr && slotTs < minBookingCutoff) return false
        return true
      })
      setSelectedTime(firstAvailable ?? timeSlots[0])
    }
  }, [timeSlots, selectedDate, selectedDayClosed, selectedDaySchedule, todayStr, minBookingCutoff, selectedTime])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const name = fullName.trim()
    const phoneTrim = phone.replace(/\D/g, '').trim()
    if (!name) {
      setError('Please enter your full name')
      return
    }
    if (!phoneTrim || phoneTrim.length < 10) {
      setError('Please enter a valid phone number')
      return
    }
    if (!selectedDate || !selectedTime) {
      setError('Please select date and time')
      return
    }
    const daySchedule = getScheduleForDay(restaurantInfo?.restaurant_schedule, selectedDate.getDay())
    const reservationDateTime = getSlotDateTime(selectedDate, selectedTime, daySchedule)

    setSubmitting(true)
    try {
      const res = await customerApi.createReservation({
        customer_name: name,
        customer_email: email.trim() || undefined,
        customer_phone: phoneTrim,
        party_size: guests,
        reservation_time: reservationDateTime.toISOString(),
        notes: undefined
      })
      if (res.success && res.data) {
        const data = res.data as { id: string }
        addRecentReservation({
          id: data.id,
          reservation_time: reservationDateTime.toISOString(),
          party_size: guests,
          customer_name: name,
          status: 'pending'
        })
        navigate('/reservation-success', { state: { from: 'reservation' } })
        return
      }
      setError(res.error || t('reservationFailed'))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('reservationFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col pb-24">
      <header className="sticky top-0 z-30 bg-[#fafafa] border-b border-gray-100 shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 -m-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={t('back')}
          >
            <ChevronLeftIcon className="w-5 h-5 rtl:scale-x-[-1]" />
          </button>
          <h1 className="text-base font-bold text-gray-800">{t('reservation')}</h1>
          <div className="w-9" aria-hidden />
        </div>
      </header>

      <form id="reservation-form" onSubmit={handleSubmit} className="flex-1 max-w-md mx-auto w-full px-4 py-6 flex flex-col gap-6">
        {/* Full name, Email, Phone */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 space-y-4">
          <label className="block">
            <span className="block text-sm font-semibold text-gray-800 mb-1">{t('fullName')} *</span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={t('name')}
              className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 outline-none"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-semibold text-gray-800 mb-1">{t('email')}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 outline-none"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-semibold text-gray-800 mb-1">{t('phoneNumberRequired')} *</span>
            <input
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 15))}
              placeholder="05xxxxxxxx"
              className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 outline-none"
            />
          </label>
        </div>

        {/* Guests */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-base font-semibold text-gray-900">{t('guests')}</span>
            <div className="flex items-center gap-2 border border-gray-200 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setGuests((g) => Math.max(MIN_GUESTS, g - 1))}
                className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100"
              >
                −
              </button>
              <span className="w-10 text-center font-medium text-gray-900">{guests}</span>
              <button
                type="button"
                onClick={() => setGuests((g) => Math.min(MAX_GUESTS, g + 1))}
                className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100"
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* Date — today through next 2 weeks; grey out past, holidays, and closed days */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
          <span className="block text-base font-semibold text-gray-900 mb-3">{t('date')}</span>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {dateRange.map((d) => {
              const ts = d.getTime()
              const isPast = ts < todayStart
              const isHoliday = holidays.has(dateToYYYYMMDD(d))
              const daySchedule = getScheduleForDay(schedule, d.getDay())
              const isClosedDay = daySchedule.closed
              const isUnavailable = isPast || isHoliday || isClosedDay
              const isSelected = selectedDate && selectedDate.getTime() === ts
              const dayNum = d.getDate()
              const dayName = DAY_NAMES_SHORT[d.getDay()]
              return (
                <button
                  key={ts}
                  type="button"
                  onClick={() => !isUnavailable && setSelectedDate(new Date(ts))}
                  disabled={isUnavailable}
                  className={`shrink-0 w-14 py-2.5 rounded-xl text-center transition-colors ${
                    isUnavailable
                      ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                      : isSelected
                        ? 'bg-gray-900 text-white font-bold'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={isHoliday ? 'Holiday' : isClosedDay ? 'Closed' : undefined}
                >
                  <div className="text-xs opacity-90">{dayName}</div>
                  <div className="text-sm font-semibold">{dayNum}</div>
                  {(isHoliday || isClosedDay) && !isPast && (
                    <div className="text-[10px] opacity-80 mt-0.5">Closed</div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Time — slots from business hours for selected day; or "Closed" message */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
          <span className="block text-base font-semibold text-gray-900 mb-3">{t('time')}</span>
          {selectedDayClosed ? (
            <p className="text-gray-500 py-4 text-center">We&apos;re closed on this day.</p>
          ) : (
            <>
              {minimumBookingHours > 0 && (
                <p className="text-xs text-gray-500 mb-2">
                  Book at least {minimumBookingHours} {minimumBookingHours === 1 ? 'hour' : 'hours'} ahead
                </p>
              )}
              <div className="grid grid-cols-3 gap-2">
                {timeSlots.map((slot) => {
              const slotDate = selectedDate && selectedDaySchedule ? getSlotDateTime(selectedDate, slot, selectedDaySchedule) : null
              const slotTs = slotDate?.getTime() ?? 0
              const isSlotToday = slotDate ? dateToYYYYMMDD(slotDate) === dateToYYYYMMDD(new Date()) : false
              const isTooSoon = isSlotToday && slotTs < minBookingCutoff
              const isSelected = selectedTime === slot
              return (
                <button
                  key={slot}
                  type="button"
                  onClick={() => !isTooSoon && setSelectedTime(slot)}
                  disabled={isTooSoon}
                  className={`py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isTooSoon
                      ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                      : isSelected
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {formatTime12h(slot)}
                </button>
              )
            })}
              </div>
            </>
          )}
        </div>

        {error && (
          <p className="text-red-600 text-sm">{error}</p>
        )}
      </form>

      {/* Bottom bar: selected date & time (left) + Reserve button (right), like checkout total + confirm */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-100 border-t border-gray-200 safe-area-pb">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-500">
              {selectedDate
                ? selectedDayClosed
                  ? `${DAY_NAMES_SHORT[selectedDate.getDay()]}, ${selectedDate.getDate()} ${selectedDate.toLocaleDateString(undefined, { month: 'short' })} — Closed`
                  : `${DAY_NAMES_SHORT[selectedDate.getDay()]}, ${selectedDate.getDate()} ${selectedDate.toLocaleDateString(undefined, { month: 'short' })}`
                : '—'}
            </p>
            <p className="text-lg font-bold text-orange-500 mt-0.5">
              {selectedDate && !selectedDayClosed && selectedTime ? formatTime12h(selectedTime) : '—'}
            </p>
          </div>
          <button
            type="submit"
            form="reservation-form"
            disabled={submitting || selectedDayClosed || !selectedTime}
            className="flex-1 min-w-0 py-3.5 rounded-xl bg-orange-500 text-white font-bold text-sm uppercase hover:bg-orange-600 transition-colors disabled:opacity-60"
          >
            {submitting ? t('loading') : t('reserve')}
          </button>
        </div>
      </div>
    </div>
  )
}
